<template>
  <div
    :class="[
      homeViewClasses,
      { 'justify-end': !isEnabledWidgetV2 && !showResources },
    ]"
    :style="{
      '--brand-text-on-white-background': readableTextColorOnWhiteBackground,
    }"
  >
    <div ref="teamAvailabilityRef" class="px-3 pt-6 w-full">
      <TeamAvailabilityV2
        v-if="isEnabledWidgetV2"
        :available-agents="availableAgents"
        :has-conversation="!!conversationSize"
        :unread-count="unreadMessageCount"
        @start-conversation="startConversation"
      />
      <team-availability
        v-else
        :available-agents="availableAgents"
        :has-conversation="!!conversationSize"
        :unread-count="unreadMessageCount"
        @start-conversation="startConversation"
      />
    </div>
    <div
      v-if="isOrderTrackingEnabled"
      ref="orderTrackingRef"
      class="px-3 pt-3 w-full"
    >
      <OrderTrackingV2
        v-if="isEnabledWidgetV2"
        :available-agents="availableAgents"
        :has-conversation="!!conversationSize"
        :unread-count="unreadMessageCount"
        @start-conversation="startConversation"
      />
      <order-tracking v-else @start-conversation="startConversation" />
    </div>
    <div v-if="showAgentTriggers" class="px-3 pt-3 w-full">
      <agent-bot-triggers @start-conversation="startConversation" />
    </div>
    <div ref="resourceListRef">
      <ResourceListV2
        v-if="isEnabledWidgetV2"
        @view-article="openArticleInArticleViewer"
      />
      <resource-list v-else @view-article="openArticleInArticleViewer" />
    </div>
  </div>
</template>

<script>
import { tv } from 'tailwind-variants';
import TeamAvailability from 'widget/components/TeamAvailability.vue';
import OrderTracking from 'widget/components/OrderTracking.vue';
import AgentBotTriggers from 'widget/components/AgentBotTriggers.vue';
import ResourceList from './ResourceList.vue';

// V2
import TeamAvailabilityV2 from 'widget/v2/components/home-parts/TeamAvailability.vue';
import OrderTrackingV2 from 'widget/v2/components/home-parts/OrderTracking.vue';
import ResourceListV2 from 'widget/v2/components/home-parts/ResourceList.vue';

import { mapGetters } from 'vuex';
import darkModeMixin from 'widget/mixins/darkModeMixin';
import routerMixin from 'widget/mixins/routerMixin';
import configMixin from 'widget/mixins/configMixin';
import { useFlipper } from 'widget/composables/useFlipper';
import { animateEnter } from 'widget/composables/useEnterAnimation';
import { darken, getContrast } from 'color2k';
import { IFrameHelper } from 'widget/helpers/utils';
import { getHomeViewHeight } from 'widget/helpers/IframeEventHelper';

const homeViewRecipe = tv({
  base: 'z-50 rounded-md w-full flex flex-col',
  variants: {
    version: {
      v1: 'flex-1 justify-end',
      v2: '',
    },
  },
  defaultVariants: {
    version: 'v1',
  },
});

export default {
  name: 'Home',
  components: {
    TeamAvailability,
    OrderTracking,
    AgentBotTriggers,
    ResourceList,
    OrderTrackingV2,
    TeamAvailabilityV2,
    ResourceListV2,
  },
  mixins: [configMixin, routerMixin, darkModeMixin],
  beforeRouteLeave(to, _from, next) {
    // Only reset height when navigating to another route (not when widget closes)
    if (to.name && to.name !== 'home') {
      IFrameHelper.sendMessage({ event: 'resetToFixedHeight' });
    }
    next();
  },
  props: {
    hasFetched: {
      type: Boolean,
      default: false,
    },
    isCampaignViewClicked: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isEnabled } = useFlipper();
    return { isFlipperEnabled: isEnabled };
  },
  computed: {
    ...mapGetters({
      availableAgents: 'agent/availableAgents',
      activeCampaign: 'campaign/getActiveCampaign',
      conversationSize: 'conversation/getConversationSize',
      unreadMessageCount: 'conversation/getUnreadMessageCount',
      resources: 'inboxChatResources/getResources',
      isOrderTrackingEnabled: 'appConfig/getOrderTrackingEnabled',
      widgetColor: 'appConfig/getWidgetColor',
      isWidgetOpen: 'appConfig/getIsWidgetOpen',
    }),
    widgetLocale() {
      return this.$i18n.locale || 'en';
    },
    showAgentTriggers() {
      return false;
    },
    showResources() {
      return true;
    },
    defaultLocale() {
      const widgetLocale = this.widgetLocale;
      const { allowed_locales: allowedLocales, default_locale: defaultLocale } =
        this.portal.config;

      // IMPORTANT: Variation strict locale matching, Follow iso_639_1_code
      // If the exact match of a locale is available in the list of portal locales, return it
      // Else return the default locale. Eg: `es` will not work if `es_ES` is available in the list
      if (allowedLocales.includes(widgetLocale)) {
        return widgetLocale;
      }
      return defaultLocale;
    },
    readableTextColorOnWhiteBackground() {
      const color = this.widgetColor || '#1f93ff';
      const contrastLevel = getContrast(color, '#fff');

      const readableColor = contrastLevel <= 1.4 ? darken(color, 0.3) : color;

      return readableColor;
    },
    isEnabledWidgetV2() {
      return this.isFlipperEnabled('chat_widget_v2').value;
    },
    homeViewClasses() {
      return homeViewRecipe({
        version: this.isEnabledWidgetV2 ? 'v2' : 'v1',
      });
    },
  },
  watch: {
    isWidgetOpen(newVal, oldVal) {
      if (newVal && !oldVal) {
        // Widget just opened (from closed -> open)
        this.onWidgetOpen();
      }
    },
    availableAgents: {
      handler() {
        if (this.isEnabledWidgetV2) {
          this.updateWidgetHeight();
        }
      },
      deep: true,
      immediate: true,
    },
    resources: {
      handler() {
        if (this.isEnabledWidgetV2) {
          this.updateWidgetHeight();
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.portal && this.popularArticles.length === 0) {
      const locale = this.defaultLocale;
      this.$store.dispatch('article/fetch', {
        slug: this.portal.slug,
        locale,
      });
    }
  },

  methods: {
    runEnterAnimations() {
      const STAGGER_DELAY = 80;
      let delay = 0;

      // TeamAvailability always exists
      animateEnter(this.$refs.teamAvailabilityRef, delay);
      delay += STAGGER_DELAY;

      // OrderTracking is conditional
      if (this.$refs.orderTrackingRef) {
        animateEnter(this.$refs.orderTrackingRef, delay);
        delay += STAGGER_DELAY;
      }

      // ResourceList is conditional (check if resources exist)
      if (this.$refs.resourceListRef && this.resources?.length > 0) {
        animateEnter(this.$refs.resourceListRef, delay);
      }
    },
    onWidgetOpen() {
      if (this.isEnabledWidgetV2) {
        this.$nextTick(() => {
          this.updateWidgetHeight();
          this.runEnterAnimations();
        });
      }
    },
    updateWidgetHeight(delay = 0) {
      setTimeout(() => {
        const contentHeight = getHomeViewHeight();
        if (contentHeight > 0) {
          IFrameHelper.sendMessage({
            event: 'setHomeViewHeight',
            contentHeight,
          });
        }
      }, delay);
    },
    startConversation() {
      if (this.preChatFormEnabled && !this.conversationSize) {
        return this.replaceRoute('prechat-form');
      }
      return this.replaceRoute('messages');
    },
    openArticleInArticleViewer(link) {
      let linkToOpen = `${link}?show_plain_layout=true`;
      const isDark = this.prefersDarkMode;
      if (isDark) {
        linkToOpen = `${linkToOpen}&theme=dark`;
      }
      this.$router.push({
        name: 'article-viewer',
        params: { link: linkToOpen },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.widget-home-bg {
  background: linear-gradient(
    161deg,
    #f4f3f5 8.3%,
    #f4f3f5 25.01%,
    #f4f3f5 58.1%,
    #e4e4fa 94.45%
  );
}

.widget-home-bg-dark {
  background: linear-gradient(
    161deg,
    #26292b 8.3%,
    #26292b 25.01%,
    #1f1f27 58.1%,
    #3c3758 94.45%
  );
}
</style>
